// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-5 §2.3 — the pot-form calls of the OwlRx Plan of Treatment. No `?schema=`: the server resolves
// the pharmacy's current instrument (CareRxPotFormFunctions.PharmacyInstrumentAsync — pot_v1 v2 unless
// the pharmacy runs POT classic); a visit that already has a form keeps the schema it was authored
// against. api/client.ts is frozen (E-05) — these ride pages/carerx/carerxPotRequest like POT classic's potApi.ts.
import type { PotPatient, PotPrefill } from '../pages/carerx/carerxPotPrefill'
import { potRequest } from '../pages/carerx/carerxPotRequest'
import type { PotCompleteFormResult } from '../pages/carerx/carerxPotSubmit'

export interface CareRxPotFormRow {
  id: string
  version: number
  status: string
  sections: string | null
  section_status: string | null
  attestation: string | null
  content_hash: string | null
  updated_at: string
  completed_at: string | null
  worked_seconds?: number | null
  /** D-CRX-89: the protocols this POT was written against (pinned at authorship), JSON. */
  pinned_refs?: string | null
}
export interface CareRxPotFormResponse {
  visit_id: string
  visit_status: string
  /** The tenant's branding name (print header/footer); null when unbranded. */
  pharmacy_name?: string | null
  schema: { id: string; code: string; version: number; scope: string; definition: unknown } | null
  form: CareRxPotFormRow | null
  patient: PotPatient | null
  prefill?: PotPrefill | null
}

/** asp-pot field modes (server.js:4404): locked / editable-with-override / suggestion / panel-only. */
export type HandoffMode = 'hard_prefill_locked' | 'hard_prefill' | 'soft_prefill' | 'reference_only'
export interface HandoffField { value: string | null; mode: HandoffMode }
/** GET carerx/visits/{id}/pot-handoff (CareRxPotHandoffFunctions) — the pharmacist worksheet projection. */
export interface CareRxPotHandoff {
  has_handoff: boolean
  pcw_id: string | null
  pcw_status: string | null
  diagnosis: (HandoffField & { icd10: string | null }) | null
  medication: HandoffField | null
  allergies: (HandoffField & { nkda: boolean }) | null
  comorbidities: HandoffField | null
  medications: HandoffField | null
  determination: (HandoffField & { signed_by: string | null; signed_at: string | null }) | null
  dosing_guidance: HandoffField | null
  care_plan_narrative: HandoffField | null
  counseling_complete: { value: boolean; method: string | null; mode: HandoffMode } | null
  pa_status: (HandoffField & { ref: string | null }) | null
  short_term_goals: HandoffField | null
  long_term_goals: HandoffField | null
  risk_flags: HandoffField | null
}
export interface DrugRequestResponse { success: boolean; request: { id: string; drug_name: string; status: string } }
export interface CareRxPotCompleteResponse extends PotCompleteFormResult {
  id: string; version: number; status: string; content_hash: string; dose_no?: number | null; cadence_reanchored?: boolean
}

export const getNativePotForm = (visitId: string) =>
  potRequest<CareRxPotFormResponse>('GET', `/carerx/visits/${visitId}/pot-form`)
export const putNativePotForm = (visitId: string, body: { sections: string; section_status: string; base_updated_at?: string | null }) =>
  potRequest<{ id: string; version: number; status: string; updated_at: string; worked_seconds?: number }>('PUT', `/carerx/visits/${visitId}/pot-form`, body)
export const completeNativePotForm = (visitId: string, body: { signature_data_url: string }) =>
  potRequest<CareRxPotCompleteResponse>('POST', `/carerx/visits/${visitId}/pot-form/complete`, body)

export const getNativePotHandoff = (visitId: string) =>
  potRequest<CareRxPotHandoff>('GET', `/carerx/visits/${visitId}/pot-handoff`)
/** asp-pot requestDrugAdd → POST carerx/formulary/requests (CareRxDrugRequestFunctions, mig 452). */
export const requestDrugAdd = (body: { drug_name: string; notes?: string }) =>
  potRequest<DrugRequestResponse>('POST', '/carerx/formulary/requests', body)
export const getAppVersion = () => potRequest<{ version: string }>('GET', '/version')

/** TanStack Query key for the form load (one entry per visit; never shared with the classic screen). */
export const nativePotFormKey = (visitId: string) => ['carerx', 'pot', 'owlrx-form', visitId] as const
export const nativePotHandoffKey = (visitId: string) => ['carerx', 'pot', 'owlrx-handoff', visitId] as const

// ── D-POT-7 — the POT tab worklist (GET carerx/pot/worklist + /summary) ─────────────────────────
export type PotWorklistView = 'queue' | 'in_progress' | 'completed' | 'voided'
/** POT classic's lists (server LegacyViews): every open visit, a nurse's unclaimed pool, every visit, and every
 *  visit not voided (the last two replaced the dashboard's newest-500 read, owner 2026-10-29). */
export type PotClassicView = 'open' | 'pool' | 'all' | 'active'
export type PotWorklistSort = 'activity' | 'visit_date' | 'patient' | 'worked'
/** Queue stage: ready (therapy, no visit yet) · unclaimed (no nurse) · assigned / claimed (a nurse, no form yet). */
export type PotQueueStage = 'ready' | 'unclaimed' | 'assigned' | 'claimed'
export interface PotWorklistRow {
  kind: 'visit' | 'ready'
  stage: PotQueueStage | null
  /** Null on a ready-to-start row (no visit yet). */
  visit_id: string | null
  therapy_id: string | null
  patient_id: string
  patient_name: string | null
  patient_initials: string | null
  mrn: string | null
  drug_name: string | null
  diagnosis: string | null
  nurse_id: string | null
  nurse_name: string | null
  status: 'scheduled' | 'claimed' | 'completed' | 'cancelled' | null
  form_status: 'none' | 'draft' | 'completed'
  visit_date: string | null
  visit_type: string | null
  created_at: string | null
  updated_at: string | null
  completed_at: string | null
  worked_seconds: number | null
  signature: 'not_sent' | 'awaiting_physician' | 'signed' | 'declined' | 'voided'
  envelope_id: string | null
  document_id: string | null
  sent_at: string | null
  signed_at: string | null
  voided_at: string | null
  void_reason: string | null
  schema_code: string | null
  agency_name: string | null
  consent_status: 'none' | 'pending' | 'complete' | null
  prescriber: string | null
  enrollment_status: string | null
}
export interface PotWorklistParams {
  view: PotWorklistView | PotClassicView
  page: number
  pageSize: number
  sort?: PotWorklistSort
  dir?: 'asc' | 'desc'
  q?: string
  nurse?: string
  /** A team's nurses (login ids). */
  nurses?: string[]
  patient?: string
  drug?: string
  /** ISO instant; rows whose last activity is older are left out. */
  since?: string
}
export interface PotWorklistPage {
  items: PotWorklistRow[]; count: number; total: number; page: number; page_size: number; total_pages: number; truncated: boolean
}
export interface PotWorklistSummary {
  counts: Record<PotWorklistView | 'open' | 'pool', number>
  avg_minutes: number
  can_void: boolean
  facets: { nurses: { id: string; name: string | null }[]; drugs: string[]; patients: { id: string; name: string | null }[]; patients_truncated: boolean }
}
export function potWorklistQuery(p: PotWorklistParams): string {
  const qs = new URLSearchParams({ view: p.view, page: String(p.page), page_size: String(p.pageSize) })
  if (p.sort) qs.set('order_by', p.sort)
  if (p.dir) qs.set('order_dir', p.dir)
  for (const [k, v] of [['q', p.q?.trim()], ['nurse', p.nurse], ['patient', p.patient], ['drug', p.drug], ['since', p.since]] as const) if (v) qs.set(k, v)
  if (p.nurses?.length) qs.set('nurses', p.nurses.join(','))
  return qs.toString()
}
export const getPotWorklist = (p: PotWorklistParams) => potRequest<PotWorklistPage>('GET', `/carerx/pot/worklist?${potWorklistQuery(p)}`)
export const getPotWorklistSummary = () => potRequest<PotWorklistSummary>('GET', '/carerx/pot/worklist/summary')
